// src/icon-system/runtime/yayinKapisi.ts — YAYIN KAPISI (aile + varyant, TEK karar)
//
// ── NİYE VAR (2026-09-26, #1102 + #1100 birleşimi) ───────────────────────
// #1102 aile düzeyinde kapı koydu (`IKON_BILGISI[*].taslak` ↔ `ikonYayinKapsami`).
// #1100 varyant düzeyinde kapı koydu (`onayBekleyenVaryantlar`) ama önizlemeyi
// `?ikonAday=on`a bağlamıştı. İki ayrı önizleme anahtarı = iki ayrı sessiz yol.
// Bu dosya iki kararı TEK fonksiyonda toplar; köprü, yükleyici ve üç adaptör
// yalnız bunu sorar.
//
// ── KARAR (owner 2026-09-26, birebir) ────────────────────────────────────
//   1. Tek yayın kararı aile + varyant durumunu BİRLİKTE değerlendirir.
//      Varsayılan kapsam `onayli`; `?ikonKapsam=tam` taslak/bekleyen
//      önizlemesini açar. `?ikonAday` yalnız kayıtlı aday aileye GEÇİŞ yapar
//      (`OzellikIkonu`), bu kapıyı AŞAMAZ — burada hiç okunmaz.
//   2. Eksik/geçersiz metadata HER kapsamda eski ikona döner. `tam` kontrolü
//      metadata doğrulamasından SONRA gelir; erken dönüşle korumayı atlamaz.
//      (Kapı (g2) bunu AST sırasıyla ölçer.)
//
// ── NE YAPMAZ ────────────────────────────────────────────────────────────
// · Motor bayrağına bakmaz: köprü/adaptör `!acik || !modul` iken bu fonksiyonu
//   ÇAĞIRMADAN `eski`ye döner → bayrak kapalı kol bayt-birebir, chunk istenmez.
// · Onay kararı vermez; derleyicinin manifestten taşıdığı iki işareti okur:
//   `taslak` (aile) ve `onayBekleyenVaryantlar` (varyant). Bir çizimi "onaylı"
//   yapan şey manifest kaydıdır (owner görsel kabulü), bu dosya değil.
// · Bekleyen listesini genişletmez/daraltmaz: bugün 3 taslak aile +
//   `composer.sticker.selected`. Diğer 25 `selected` için kayıt YOK → onaylı
//   aile olarak çizilir (owner kararı bekleniyor; sessiz değişiklik yok).
// · Ad listesi kopyalamaz: hangi ailenin taslak/bekleyen olduğu YALNIZ chunk'ın
//   içindeki tablodan okunur (ikinci doğruluk kaynağı yok — kapı (g2) ölçer).
import { ikonYayinKapsami } from "./ikonBayragi";
import type { MotorModulu } from "./motorYukleyici";

export type YayinKarari =
  /** Onaylı aile + onaylı varyant → her kapsamda motor. */
  | "yayin"
  /** Taslak aile ya da bekleyen varyant → yalnız `tam` kapsamda motor. */
  | "bekliyor"
  /** Aile chunk'ta yok ya da metadata eksik/geçersiz → her kapsamda eski. */
  | "metadata-yok";

/**
 * Aile + varyant için yayın kararı. SAF: yalnız modül metadatası okur; kapsam
 * bayrağını `bekliyor` hâlinde çağıran sorar (`motorYayinaUygun`).
 */
/** Varyant adı: boş olmayan dizge. (`"selectd"` gibi yazım hatası aşağıda ÜYELİKLE yakalanır.) */
const gecerliVaryantAdi = (v: unknown): v is string => typeof v === "string" && v.length > 0;

export function yayinKarari(m: MotorModulu, aile: string, varyant = "outline"): YayinKarari {
  const id = m.aileAdiCoz(aile);
  const bilgi = id ? m.IKON_BILGISI[id] : undefined;
  // (2) Metadata doğrulaması ÖNCE. Eski chunk (derleyici <v4) `onayBekleyenVaryantlar`
  // taşımaz → dizi değil → eski ikon. Eksik metadata onay SAYILMAZ.
  // ÜYE doğrulaması (ChatGPT kod incelemesi, 2026-09-27): yalnız `Array.isArray` yetmez —
  // `onayBekleyenVaryantlar: ["selectd"]` derleyicide reddedilir ama çalışma zamanı
  // sözleşmesi ("geçersiz metadata her kapsamda eski") derleyiciye güvenmemeli.
  // Her `varyantlar` üyesi boş olmayan dizge; her bekleyen üye geçerli dizge VE
  // `varyantlar` içinde. Aksi → `metadata-yok` (onayli'da da `tam`da da eski).
  if (
    !bilgi ||
    typeof bilgi.taslak !== "boolean" ||
    !Array.isArray(bilgi.varyantlar) ||
    bilgi.varyantlar.length === 0 ||
    !bilgi.varyantlar.every(gecerliVaryantAdi) ||
    !Array.isArray(bilgi.onayBekleyenVaryantlar) ||
    !bilgi.onayBekleyenVaryantlar.every((v) => gecerliVaryantAdi(v) && bilgi.varyantlar.includes(v))
  ) {
    return "metadata-yok";
  }
  // Tek-varyantlı aile `selected` istenirse üretilmiş bileşen zaten outline'ı
  // çizer (mevcut güvenli geri dönüş); karar o GERÇEK varyant üzerinden verilir.
  const gercekVaryant = bilgi.varyantlar.includes(varyant) ? varyant : bilgi.varyantlar[0];
  if (bilgi.taslak || bilgi.onayBekleyenVaryantlar.includes(gercekVaryant)) return "bekliyor";
  return "yayin";
}

/**
 * Motor bu aile+varyantı ÇİZEBİLİR mi? `yayin` → evet · `bekliyor` → yalnız
 * kapsam `tam` · `metadata-yok` → hayır (kapsamdan bağımsız).
 * Kapsam okuması SON adımdır: metadata kararından önce hiçbir erken dönüş yok.
 */
export function motorYayinaUygun(m: MotorModulu, aile: string, varyant = "outline"): boolean {
  const karar = yayinKarari(m, aile, varyant);
  if (karar === "yayin") return true;
  if (karar === "metadata-yok") return false;
  return ikonYayinKapsami() === "tam";
}
